import { useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Form,
  Input,
  Select,
  Row,
  Col,
  Typography,
  FormInstance,
  Checkbox,
  Dropdown,
  Menu,
  Tooltip,
  Popover,
} from 'antd';

import { useTaxRegimes } from '@/core/domain/enterprise/TaxRegime/TaxRegime.hooks';

import FloatingFormItem from '@/components/_common/FloatingFormItem';
import ZipFormField from '@/components/_common/ZipFormField';
import CnpjFormField from '@/components/_common/CnpjFormField';
import { useDefaultMaxLengthFieldError } from '@/hooks';
import { Address } from '@/core/domain/_shared/Address';
import CompanyGeneralInfoContactForm from './CompanyGeneralInfoContactForm';
import { Group, ImagePickerFormField, InputText } from '@/components/_common';
import useAddressQueries from '@/hooks/useAddressQuery';
import { Info } from 'react-feather';
import { useCompanyFormContext } from '../CompaniesFormProvider';
import {
  useDeleteLogoCompany,
  useUpdateLogoCompany,
} from '@/core/domain/enterprise/Companies/Companies.hooks';
import { APP_NAME } from '@/index';

const { Title } = Typography;

interface Props {
  form: FormInstance;
  address?: Address;
}

const CompanyGeneralInfoForm = ({ form, address }: Props) => {
  const { companyId } = useCompanyFormContext();
  const isEdit = !!companyId;

  const intl = useIntl();

  const defaultMaxLenghtFieldError = useDefaultMaxLengthFieldError();
  const defaultEmptyFieldError = {
    required: true,
    message: intl.formatMessage({
      id: 'commons.form.errors.requiredField',
    }),
  };

  const updateLogoCompany = useUpdateLogoCompany();
  const deleteLogoCompany = useDeleteLogoCompany();

  const logoField = Form.useWatch(['logo'], form) || [];
  const watchZip = Form.useWatch(['companyAddress', 'zip'], form);
  const watchCity = Form.useWatch(['companyAddress', 'cityId'], form);
  const canReceiveScheduleRequest = Form.useWatch(
    'canReceiveScheduleRequest',
    form,
  );

  const { data: taxRegimes, isFetching: isFetchingTaxRegimes } =
    useTaxRegimes();

  const { state, city, zipCode } = useAddressQueries();

  useEffect(() => {
    const { companyAddress } = form.getFieldsValue();

    if (companyAddress?.zip) {
      zipCode.setSelectedZipCode(companyAddress?.zip);
    }

    if (zipCode.data) {
      state.setSelectedStateCode(zipCode.data?.state);

      form.setFieldsValue({
        companyAddress: {
          cityId: zipCode.data?.cityId,
          street: zipCode.data?.street || address?.street,
          cityName: zipCode.data?.cityName,
          district: zipCode.data?.district || address?.district,
          state: zipCode.data?.state,
          number: address?.number,
          complement: address?.complement,
        },
      });
    }
  }, [zipCode.data, form]);

  useEffect(() => {
    if (city.selectedCity) {
      state.setSelectedStateCode(city.selectedCity.state);

      form.setFieldsValue({
        companyAddress: {
          cityId: city.selectedCity.id,
          state: city.selectedCity.state,
        },
      });
    }
  }, [city.selectedCity]);

  useEffect(() => {
    if (watchZip && watchZip.length >= 8) {
      zipCode.setSelectedZipCode(watchZip);
    } else {
      zipCode.setSelectedZipCode(undefined);

      form.setFieldsValue({
        companyAddress: {
          state: null,
          cityId: null,
          district: null,
          street: null,
          number: null,
          complement: null,
        },
      });
      return;
    }
  }, [watchZip]);

  const handleRemoveLogo = async () => {
    if (companyId) {
      await deleteLogoCompany.mutateAsync(companyId);
    }
  };

  const handleUpdateLogo = async (logo: File) => {
    try {
      if (companyId && logo) {
        await updateLogoCompany.mutateAsync({
          companyId,
          file: logo,
        });
      }
    } catch {
      form.resetFields(['logo']);
    }
  };

  return (
    <Form layout="vertical" form={form} name="companyGeneral">
      <Form.Item hidden name="id">
        <Input />
      </Form.Item>

      <Row gutter={[16, 8]}>
        <Col xs={24} sm={12} md={12}>
          <FloatingFormItem
            name="name"
            rules={[defaultEmptyFieldError, defaultMaxLenghtFieldError(255)]}
            label={<FormattedMessage id="companies.general.form.name" />}
          >
            <Input />
          </FloatingFormItem>
        </Col>

        <Col xs={24} sm={12} md={12}>
          <FloatingFormItem
            name="nickName"
            rules={[defaultEmptyFieldError, defaultMaxLenghtFieldError(255)]}
            label={<FormattedMessage id="companies.general.form.fantasyName" />}
          >
            <Input />
          </FloatingFormItem>
        </Col>

        <Col xs={24} sm={12} md={8}>
          <FloatingFormItem
            name="companyType"
            label={<FormattedMessage id="companies.general.form.companyType" />}
            rules={[defaultEmptyFieldError]}
          >
            <Select style={{ width: '100%' }}>
              <Select.Option value="MECANICA">Mecânica</Select.Option>
              <Select.Option value="FUNILARIA">Funilaria</Select.Option>
            </Select>
          </FloatingFormItem>
        </Col>

        <Col xs={24} sm={12} md={3}>
          <FloatingFormItem
            name="cnae"
            label={<FormattedMessage id="companies.general.form.cnae" />}
            rules={[defaultMaxLenghtFieldError(9)]}
          >
            <InputText onlyNumber />
          </FloatingFormItem>
        </Col>

        <Col xs={24} sm={12} md={5}>
          <FloatingFormItem
            name="webSite"
            rules={[defaultMaxLenghtFieldError(255)]}
            label={<FormattedMessage id="companies.general.form.webSite" />}
          >
            <Input type="url" />
          </FloatingFormItem>
        </Col>

        <Col xs={24} sm={12} md={8}>
          <Popover
            content={
              isEdit &&
              `Para atualizar o CNPJ, entre em contato com o nosso suporte da plataforma ${APP_NAME}.`
            }
          >
            <div>
              <CnpjFormField fieldName="cnpj" disabled={isEdit} />
            </div>
          </Popover>
        </Col>

        <Col xs={24} sm={12} md={8}>
          <FloatingFormItem
            name="municipalRegistration"
            label={
              <FormattedMessage id="companies.general.form.municipalRegistration" />
            }
            rules={[defaultMaxLenghtFieldError(30)]}
          >
            <Input />
          </FloatingFormItem>
        </Col>

        <Col xs={24} sm={12} md={8}>
          <FloatingFormItem
            name="stateRegistration"
            label={
              <FormattedMessage id="companies.general.form.stateRegistration" />
            }
            rules={[defaultMaxLenghtFieldError(30)]}
          >
            <Input />
          </FloatingFormItem>
        </Col>

        <Col xs={24} sm={12} md={8}>
          <FloatingFormItem
            name="taxRegimeId"
            label={<FormattedMessage id="companies.general.form.taxRegime" />}
          >
            <Select style={{ width: '100%' }} loading={isFetchingTaxRegimes}>
              {taxRegimes?.map((a) => (
                <Select.Option key={a.id} value={a.id}>
                  {a.taxRegime}
                </Select.Option>
              ))}
            </Select>
          </FloatingFormItem>
        </Col>

        <Col xs={24} sm={12} md={4}>
          <ZipFormField
            disable={false}
            fieldName={['companyAddress', 'zip']}
            label={<FormattedMessage id="companies.general.form.cep" />}
          />
        </Col>

        <Col xs={24} sm={12} md={4}>
          <FloatingFormItem
            name={['companyAddress', 'state']}
            rules={[defaultEmptyFieldError]}
            label={<FormattedMessage id="companies.general.form.uf" />}
          >
            <Select
              allowClear
              style={{ width: '100%' }}
              options={state.list.map((s) => ({
                value: s.code,
                label: s.code,
              }))}
              disabled
            />
          </FloatingFormItem>
        </Col>

        <Col xs={24} sm={12} md={8}>
          <FloatingFormItem
            name={['companyAddress', 'cityId']}
            rules={[defaultEmptyFieldError]}
            label={<FormattedMessage id="companies.general.form.city" />}
          >
            <Select
              allowClear
              style={{ width: '100%' }}
              options={city.list.map((c: { id: number; name: string }) => ({
                value: c.id,
                label: c.name,
              }))}
              disabled
            />
          </FloatingFormItem>
        </Col>

        <Col xs={24} sm={12} md={8}>
          <FloatingFormItem
            name={['companyAddress', 'district']}
            rules={[defaultEmptyFieldError, defaultMaxLenghtFieldError(100)]}
            label={<FormattedMessage id="companies.general.form.district" />}
          >
            <Input disabled={!watchCity} />
          </FloatingFormItem>
        </Col>

        <Col xs={24} sm={12} md={10}>
          <FloatingFormItem
            name={['companyAddress', 'street']}
            rules={[defaultEmptyFieldError, defaultMaxLenghtFieldError(100)]}
            label={<FormattedMessage id="companies.general.form.street" />}
          >
            <Input disabled={!watchCity} />
          </FloatingFormItem>
        </Col>

        <Col xs={24} sm={12} md={4}>
          <FloatingFormItem
            name={['companyAddress', 'number']}
            rules={[defaultEmptyFieldError, defaultMaxLenghtFieldError(5)]}
            label={<FormattedMessage id="companies.general.form.number" />}
          >
            <Input disabled={!watchCity} />
          </FloatingFormItem>
        </Col>

        <Col xs={24} sm={12} md={10}>
          <FloatingFormItem
            name={['companyAddress', 'complement']}
            label={<FormattedMessage id="companies.general.form.complement" />}
            rules={[defaultMaxLenghtFieldError(100)]}
          >
            <Input disabled={!watchCity} />
          </FloatingFormItem>
        </Col>

        <Col xs={24} sm={24} md={15}>
          <FloatingFormItem
            name="comments"
            label={<FormattedMessage id="companies.general.form.observation" />}
            rules={[defaultMaxLenghtFieldError(500)]}
          >
            <Input.TextArea rows={4} />
          </FloatingFormItem>
        </Col>

        <Col xs={24} sm={24} md={9}>
          <ImagePickerFormField
            saveToArchive={false}
            maxFileCount={1}
            fieldName="logo"
            label={`${
              logoField.length > 0 ? 'Trocar logomarca' : 'Adicionar logomarca'
            }`}
            isCrop
            handleRemoveLogo={handleRemoveLogo}
            handleUpdateLogo={handleUpdateLogo}
          />
        </Col>

        <Col span={24}>
          <Group gap={12}>
            <Form.Item
              name="main"
              initialValue={false}
              valuePropName="checked"
              noStyle
            >
              <Checkbox>Empresa principal</Checkbox>
            </Form.Item>

            <Tooltip title="Marque esta opção se esta for a empresa principal associada a Matriz">
              <Info size={20} id="note-type-icon" cursor="pointer" />
            </Tooltip>
          </Group>
        </Col>

        <Col span={12}>
          <Group gap={12}>
            <Form.Item
              name="canReceiveScheduleRequest"
              initialValue={false}
              valuePropName="checked"
              noStyle
            >
              <Checkbox>
                Habilitar empresa para recebimento de agendamento
              </Checkbox>
            </Form.Item>

            <Dropdown
              overlay={
                <Menu
                  mode="vertical"
                  style={{ width: 300 }}
                  title="Área do cliente"
                  items={[
                    {
                      key: 1,
                      title: 'Área do cliente',
                      label: (
                        <>
                          Ao habilitar essa opção, sua empresa estará disponível
                          para que os clientes solicitem agendamentos
                          diretamente pela <strong>Área do Cliente</strong>.
                        </>
                      ),
                    },
                  ]}
                />
              }
              trigger={['click']}
              arrow
              placement="bottom"
            >
              <Info size={20} id="note-type-icon" cursor={'pointer'} />
            </Dropdown>
          </Group>
        </Col>

        {canReceiveScheduleRequest && (
          <Col span={12}>
            <FloatingFormItem
              name="notificationEmail"
              label="Email pra receber as notificações"
              rules={[
                {
                  type: 'email',
                  message: intl.formatMessage({
                    id: 'commons.form.errors.invalidEmail',
                  }),
                },
                defaultEmptyFieldError,
              ]}
            >
              <Input />
            </FloatingFormItem>
          </Col>
        )}

        <Col span={24}>
          <Title level={3}>
            <FormattedMessage id="companies.general.form.contacts" />
          </Title>
          <CompanyGeneralInfoContactForm form={form} />
        </Col>
      </Row>
    </Form>
  );
};

export default CompanyGeneralInfoForm;
